import { useEffect, useRef, useState } from "react";
import RightTimeApi from "@/API";
import "@style/Admin.scss";
import Enums from "@/enums";
import { prefetchWhenIdle } from "@/utils/runWhenIdle";
import { fetchCompanyJobsTabMerged } from "@/utils/companyJobsTabApi";
import JobList from "@/elements/subcomponents/JobList";

export default function CompanyJobsTab(props: any) {
  const { company, updateSpinner, activeTab, adminPerms } = props.tabData;

  const [data, updateData] = useState<any>({});
  const [contextLoaded, setContextLoaded] = useState(false);
  const contextRequestRef = useRef(0);

  function isOnThisTab() {
    return activeTab && activeTab === "jobs";
  }

  useEffect(() => {
    if (isOnThisTab())
      loadInitialAsync();
  }, [activeTab]);

  if (!isOnThisTab()) return null;

  if (!adminPerms?.includes(Enums.ROLE_PERMS.CompanyMenuJobs)) {
    return (
      <div className="companyJobsTab">
        <div className="tableWrapper factTable">
          <div className="noData">You do not have permission to view this content.</div>
        </div>
      </div>
    );
  }

  function toRecruitDashboard(companyJobsData: any) {
    const jobs = Array.isArray(companyJobsData?.jobs) ? companyJobsData.jobs : [];
    return {
      jobs: jobs.map((job: any) => ({
        ...job,
        descCount: Array.isArray(job?.descriptions)
          ? job.descriptions.length
          : (Number(job?.descCount) || 0)
      }))
    };
  }

  async function loadContextAsync(force = false) {
    if (!force && (contextLoaded || !company?.id)) return;
    const requestId = ++contextRequestRef.current;
    try {
      const resp = await RightTimeApi.getAsync(`/CompanyJobsTab/${company.id}/context`);
      if (requestId !== contextRequestRef.current || !resp.ok) return;
      updateData((prev: any) => ({
        ...prev,
        ...resp.jsonData,
        jobs: prev.jobs,
        permissions: prev.permissions,
        jobStates: prev.jobStates,
        jobDescriptionStates: prev.jobDescriptionStates,
      }));
      setContextLoaded(true);
    } catch (error) {
      console.error("Error loading jobs context:", error);
    }
  }

  async function prefetchContextAsync() {
    await loadContextAsync(false);
  }

  async function loadInitialAsync() {
    if (!company?.id) return;
    updateSpinner(true);
    try {
      const { ok, jsonData, contextResp } = await fetchCompanyJobsTabMerged(company.id);
      if (ok && jsonData) {
        const contextOk = Boolean(contextResp?.ok && contextResp.jsonData);
        updateData(jsonData);
        setContextLoaded(contextOk);
        if (!contextOk) {
          prefetchWhenIdle(() => prefetchContextAsync());
        }
      }
    } finally {
      updateSpinner(false);
    }
  }

  async function getCompanyJobsAsync() {
    if (!company.id) return null;
    updateSpinner(true);
    try {
      const { ok, jsonData, contextResp } = await fetchCompanyJobsTabMerged(company.id);
      if (ok && jsonData) {
        updateData(jsonData);
        setContextLoaded(Boolean(contextResp?.ok && contextResp.jsonData));
        return jsonData;
      }
    } finally {
      updateSpinner(false);
    }
    return null;
  }

  if (isOnThisTab() && data?.permissions) {
    const recruitDashboard = toRecruitDashboard(data);
    return (
      <div className="companyJobsTab">
        <JobList
          company={company}
          companyJobs={data}
          recruitDashboard={recruitDashboard}
          adminPerms={adminPerms}
          updateSpinner={updateSpinner}
          refreshCompanyJobs={getCompanyJobsAsync}
          refreshRecruitDashboard={async () => toRecruitDashboard(await getCompanyJobsAsync())}
          workflowItem={{ id: "jobs" }}
          legacyColumnLayout={true}
          onSelectedJobChange={(jobId) => {
            if (jobId && !contextLoaded) {
              loadContextAsync(true);
            }
          }}
        />
      </div>
    );
  } else {
    return null;
  }
}
